import { ref } from 'vue'
import router from '@/router'
import { sendSecureLink, exchangeTokenForJWT } from '@/services/authService'
import { useAuthStore } from '@/stores/authStore'
import { useUserStore } from '@/stores/userStore'
import { useInfoStore } from '@/stores/info'
import i18n, { tGlobal } from '../../i18n'

const LOCALE_STORAGE_KEY = 'tpa_locale'

export function useAuth() {
  const email = ref('')
  const language = ref('en')
  const successMessage = ref('')
  const errorMessage = ref('')

  const authStore = useAuthStore()
  const userStore = useUserStore()
  const infoStore = useInfoStore()

  const sendLink = async (email: string, language: string) => {
    successMessage.value = ''
    errorMessage.value = ''

    try {
      const response = await sendSecureLink(email, language)
      const data = response.data as { message: string }
      successMessage.value = data.message
    } catch (err: any) {
      errorMessage.value = err.response?.data?.error || tGlobal('signin_view.error_message')
      throw err
    }
  }

  const login = async (token: string, language: string) => {
    try {
      const response = await exchangeTokenForJWT(token, language)
      // Type assertion for expected response shape
      const data = response.data as { accessToken: string; email: string }
      const { accessToken, email } = data

      authStore.setToken(accessToken) // Store JWT in Pinia or your state management
      // Directly store user in authStore if needed
      authStore.setUser(email) // Adjust based on your user structure
    } catch (error: any) {
      console.error('Login failed', error)
      errorMessage.value = error.response?.data?.error || 'Invalid or expired token.'
      return false
    }
    try {
      await userStore.loadCurrentUser()
    } catch (error) {
      infoStore.trigger(tGlobal('signIn.userDataError'), 'error')
    }

    const user = userStore.currentUser
    const targetLocale =
      user?.organization?.language_record?.slug ||
      user?.language ||
      ''
    if (targetLocale) {
      i18n.global.locale.value = targetLocale as typeof i18n.global.locale.value
      try {
        localStorage.setItem(LOCALE_STORAGE_KEY, targetLocale)
      } catch {
        // ignore - private mode / disabled storage; locale change still
        // applied in-memory.
      }
    }

    return true
  }

  const clearStorage = async () => {
    // Preserve a few keys across logout so they survive re-login:
    //   - session-ended popup flags, so the popup doesn't re-appear.
    //   - questionnaire drafts (scoped per userId:teamId:sessionId), so users
    //     resume where they left off instead of restarting.
    //   - per-member reminder counts (scoped per teamId:sessionId), so the
    //     "N recordatorios enviados" badge isn't reset by a logout.
    const preservedEntries: [string, string][] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (
        key?.startsWith('session-ended-shown-') ||
        key?.startsWith('tpa:questionnaire-draft:') ||
        key?.startsWith('tpa:reminder-counts:')
      ) {
        preservedEntries.push([key, localStorage.getItem(key) ?? ''])
      }
    }

    userStore.clear()
    authStore.clear()
    infoStore.clear()

    localStorage.clear()
    sessionStorage.clear()

    for (const [key, value] of preservedEntries) {
      localStorage.setItem(key, value)
    }
  }

  const logout = async ({ redirect = true }: { redirect?: boolean } = {}) => {
    await clearStorage()
    if (redirect) {
      router.push({ path: '/signin' })
    }
  }

  return {
    email,
    language,
    successMessage,
    errorMessage,
    logout,
    sendLink,
    login,
  }
}
